import React, { useMemo, useCallback, useState, useEffect, useRef } from "react";
import { Box, Typography, Chip, IconButton, Tooltip, Button } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Maestro } from "@ae/maestrov2-form-lib";
import { bindData } from "@aelive/data-binding-library";

const DDS_STORAGE_KEY = "opus_dds_data_model";
const UI_SETTINGS_KEY = "maestro-ui-settings";

const PREVIEW_UI_SETTINGS = {
  hideDataSourceFields: false,
  hideCaptionDrivenFields: false,
  showDebugBorders: false,
  debugMode: false,
  showImportPanel: false,
  showVuiEditor: false,
  showContextInspector: false,
};

function cleanFormData(raw) {
  if (!raw || typeof raw !== "object") return {};
  const clean = {};
  for (const [k, v] of Object.entries(raw)) {
    if (k === "captions" || k === "captionType" || k === "selectedDefRef") continue;
    clean[k] = v;
  }
  return clean;
}

function readShowJsonViewer() {
  try {
    const raw = localStorage.getItem(UI_SETTINGS_KEY);
    if (raw) return !!JSON.parse(raw).showJsonViewer;
  } catch { /* ignore */ }
  return false;
}

const MaestroFormPreview = ({ maestroJson, dataModel, captionFiles, selectedCaption }) => {
  const [originalUiSettings] = useState(() => {
    const backup = localStorage.getItem(UI_SETTINGS_KEY);
    localStorage.setItem(UI_SETTINGS_KEY, JSON.stringify({ ...PREVIEW_UI_SETTINGS, showJsonViewer: false }));
    return backup;
  });

  useEffect(() => {
    return () => {
      if (originalUiSettings) {
        localStorage.setItem(UI_SETTINGS_KEY, originalUiSettings);
      } else {
        localStorage.removeItem(UI_SETTINGS_KEY);
      }
    };
  }, [originalUiSettings]);

  // Seed from the caption selected in the editor so opening the preview defaults
  // to that caption. MaestroFormPreview remounts each time the preview opens, so
  // this re-picks up the current selection on every open. In-preview navigation
  // then updates the ref (via onFormDataChange) and survives Refresh.
  const lastCaptionRef = useRef(selectedCaption ?? null);
  const [maestroKey, setMaestroKey] = useState(0);
  const [liveFormData, setLiveFormData] = useState({});
  const [copied, setCopied] = useState(false);
  const [showJson, setShowJson] = useState(false);

  // Poll localStorage to detect when Maestro's gear toggle changes showJsonViewer.
  // Maestro writes to localStorage on every settings toggle but doesn't expose a callback.
  useEffect(() => {
    const poll = setInterval(() => {
      const current = readShowJsonViewer();
      setShowJson((prev) => (prev !== current ? current : prev));
    }, 300);
    return () => clearInterval(poll);
  }, []);

  // Follow the editor's caption selection: when it changes while the preview is
  // open, point Maestro at the newly-selected caption and remount to apply it.
  // Keyed on selectedCaption only, so in-preview navigation (which updates the
  // ref via onFormDataChange) is preserved and doesn't trigger a remount.
  useEffect(() => {
    if (selectedCaption && selectedCaption !== lastCaptionRef.current) {
      lastCaptionRef.current = selectedCaption;
      setMaestroKey((k) => k + 1);
    }
  }, [selectedCaption]);

  const handleRefresh = useCallback(() => {
    const currentShowJson = readShowJsonViewer();
    localStorage.setItem(
      UI_SETTINGS_KEY,
      JSON.stringify({ ...PREVIEW_UI_SETTINGS, showJsonViewer: currentShowJson })
    );
    setMaestroKey((k) => k + 1);
  }, []);

  const handleCopy = useCallback(() => {
    try {
      navigator.clipboard.writeText(JSON.stringify(cleanFormData(liveFormData), null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  }, [liveFormData]);

  const [ddsContext, setDdsContext] = useState(() => {
    if (dataModel) return dataModel;
    try {
      const stored = localStorage.getItem(DDS_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed?.rawData || null;
      }
    } catch { /* ignore */ }
    return null;
  });

  useEffect(() => {
    if (dataModel) {
      setDdsContext(dataModel);
      return;
    }
    const onModelSaved = () => {
      try {
        const stored = localStorage.getItem(DDS_STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          setDdsContext(parsed?.rawData || null);
        }
      } catch { /* ignore */ }
    };
    window.addEventListener("opus-dds-model-saved", onModelSaved);
    return () => window.removeEventListener("opus-dds-model-saved", onModelSaved);
  }, [dataModel]);

  const dataService = useMemo(
    () => ({
      getContext: async () => ddsContext || {},
      evaluate: async (expr, context, options) => {
        try {
          if (typeof expr === "function") return expr(context?.data || context, options);
          if (typeof expr === "string" && expr.includes("=>")) {
            const fn = new Function("data", "options", `return ${expr}`);
            return fn(context?.data || context, options);
          }
          return expr;
        } catch {
          return expr;
        }
      },
    }),
    [ddsContext]
  );

  const brokerService = useMemo(
    () => ({
      send: async (cmd) => {
        console.log("[FormPreview] Action triggered (preview-only):", cmd?.caption?.action, cmd);
      },
      getState: () => ({}),
    }),
    []
  );

  const onFormDataChange = useCallback(({ caption, formData }) => {
    if (caption) lastCaptionRef.current = caption;
    setLiveFormData(formData || {});
  }, []);

  const hasSchema = !!(maestroJson && maestroJson.schema);

  if (!hasSchema) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          No schema available for preview.
        </Typography>
      </Box>
    );
  }

  const cleanData = cleanFormData(liveFormData);

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Toolbar */}
      <Box
        sx={{
          px: 1,
          py: 0.5,
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {ddsContext && (
            <Chip label="DDS Data Active" size="small" color="success" variant="outlined" sx={{ fontSize: 10, height: 20 }} />
          )}
        </Box>
        <Tooltip title="Refresh preview with latest changes">
          <IconButton size="small" onClick={handleRefresh} sx={{ color: "primary.main" }}>
            <RefreshIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Main content: form + JSON viewer controlled by Maestro's gear toggle */}
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Box sx={{ flex: showJson ? "0 0 50%" : 1, minHeight: 0, overflow: "auto" }}>
          <Maestro
            key={maestroKey}
            open={true}
            onClose={() => {}}
            graphicId={maestroJson.id || "preview"}
            graphicName={maestroJson.name || "Preview"}
            schema={maestroJson.schema}
            uiSchema={maestroJson.v_ui || {}}
            captionsSchema={maestroJson.v_captions || undefined}
            actionsSchema={maestroJson.v_actions || {}}
            controllersConfig={maestroJson.v_config || undefined}
            captions={captionFiles}
            mount="inline"
            initialCaption={lastCaptionRef.current}
            dataService={dataService}
            brokerService={brokerService}
            bindData={bindData}
            onFormDataChange={onFormDataChange}
            defaultUiSettings={{ ...PREVIEW_UI_SETTINGS, showJsonViewer: showJson }}
          />
        </Box>

        {/* JSON Viewer — shown when user toggles "Show JSON Viewer" in Maestro's gear menu */}
        {showJson && (
          <Box
            sx={{
              flex: "0 0 50%",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              borderTop: "1px solid #30363d",
              bgcolor: "#0d1117",
            }}
          >
            <Box
              sx={{
                px: 1,
                py: 0.5,
                borderBottom: "1px solid #30363d",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Cascadia Code, monospace",
                  fontSize: "0.75rem",
                  color: "#4FC3F7",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Live Payload
              </Typography>
              <Button
                size="small"
                startIcon={<ContentCopyIcon sx={{ fontSize: 12 }} />}
                onClick={handleCopy}
                sx={{
                  fontFamily: "Cascadia Code, monospace",
                  fontSize: "0.75rem",
                  textTransform: "none",
                  minWidth: 0,
                  px: 1,
                  color: copied ? "#4FC3F7" : "#aaa",
                }}
              >
                {copied ? "Copied" : "Copy"}
              </Button>
            </Box>
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                p: 1,
                "&::-webkit-scrollbar": { width: 6 },
                "&::-webkit-scrollbar-thumb": { bgcolor: "#30363d", borderRadius: 3 },
              }}
            >
              <pre
                style={{
                  margin: 0,
                  fontFamily: "Cascadia Code, monospace",
                  fontSize: "0.75rem",
                  lineHeight: 1.5,
                  color: "#e6edf3",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {Object.keys(cleanData).length > 0
                  ? JSON.stringify(cleanData, null, 2)
                  : "// Select a caption type to see live payload data"}
              </pre>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MaestroFormPreview;
